import React, {
  useMemo,
  useEffect,
  useCallback,
  ChangeEvent,
  useRef,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { CheckboxBlockList } from '../CheckboxBlockList';
import { Select, OptionsObject } from './Select';
import {
  supportedLocales,
  supportedWrittenLocales,
  countryLocales,
  availableLocales,
} from '@app/api';
import { CheckboxBlockField } from '../CheckboxBlockField';
import { useOptionalField } from '../FormikFields';
import { useFormikContext } from 'formik';

const useStyles = createUseStyles((theme: Theme) => ({
  checkboxLine: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginRight: -theme.marginBase,
  },
  checkbox: {
    maxWidth: '100%',
  },
}));

interface Props {
  name?: string;
  label?: string;
  secondaryLabel?: string;
  restricted?: boolean;
  countries?: string[];
  locales?: string[];
  isMulti?: boolean;
  value?: string[] | string;
  onChange?: (val?: OptionsObject[] | OptionsObject) => any;
  written?: boolean;
}

const supportedLocalesOptions = supportedLocales.map((locale) => ({
  label: 'locales.' + locale,
  value: locale,
}));

const writtenLocalesOptions = supportedWrittenLocales.map((locale) => ({
  label: 'locales.' + locale,
  value: locale,
}));

export const getLocalesFromCountries = (countries?: string[]) => {
  const locales: string[] = [];
  if (!countries || !countries.length) {
    return ['en'];
  }
  const firstLocales = countryLocales[countries[0]] || [];
  firstLocales.forEach((locale) => {
    let inEveryCountry = true;
    countries.forEach((countryKey: string) => {
      const foundLocales = countryLocales[countryKey] || [];
      if (!foundLocales) {
        inEveryCountry = false;
        return;
      }
      if (!foundLocales.includes(locale)) {
        inEveryCountry = false;
        return;
      }
    });
    if (inEveryCountry) {
      locales.push(locale);
    }
  });

  if (!locales.includes('en')) {
    locales.push('en');
  }

  return locales;
};

export const LocaleSelect = ({
  isMulti,
  label,
  restricted,
  countries,
  name,
  value: propsValues,
  onChange,
  written,
  locales,
  ...props
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const allLocalesOptions = useMemo(() => {
    const options = written ? writtenLocalesOptions : supportedLocalesOptions;
    if (!locales) {
      return options;
    }
    return options.filter((option) => locales.includes(option.value));
  }, [written, locales]);

  const {
    field: { value: formikValue },
    helpers,
  } = useOptionalField({ name });
  const formik = useFormikContext();

  const value = formikValue || propsValues;

  const restrictedLocales = useMemo(
    () => getLocalesFromCountries(countries),
    [countries],
  );

  const setFieldValue = formik && formik.setFieldValue;
  const _onChange = useCallback(
    (val?: OptionsObject | OptionsObject[]) => {
      if (onChange) {
        onChange(val);
      }

      if (setFieldValue && name) {
        if (!val) {
          setFieldValue(name, isMulti ? [] : '');
        } else if (Array.isArray(val)) {
          setFieldValue(
            name,
            val.map((option) => option.value),
          );
        } else {
          setFieldValue(name, val.value);
        }
      }
    },
    [setFieldValue, name, onChange, isMulti, helpers],
  );

  const onLocaleCheck = useCallback(
    (val: ChangeEvent<HTMLInputElement>) => {
      const { value: newValue, checked } = val.currentTarget;
      const current = isMulti
        ? value.map((v: string) => allLocalesOptions.find((o) => o.value === v))
        : value;

      if (checked) {
        const option = allLocalesOptions.find((o) => o.value === newValue);
        if (isMulti) {
          _onChange([...current, option]);
        } else {
          _onChange(option);
        }
      } else {
        if (isMulti) {
          _onChange(current.filter((o: OptionsObject) => o.value !== newValue));
        } else {
          _onChange();
        }
      }
    },
    [isMulti, _onChange, value, allLocalesOptions],
  );

  const allowedLocales = useMemo(() => {
    if (restricted) {
      return restrictedLocales;
    }
    return supportedLocales;
  }, [restricted, restrictedLocales]);

  const previousCountriesRef = useRef(countries);

  // Prevent recompute useEffect on onChangeChange
  const onChangeRef = useRef(_onChange);
  onChangeRef.current = _onChange;

  useEffect(() => {
    const previousCountries = previousCountriesRef.current;
    previousCountriesRef.current = countries;

    if (!value || (Array.isArray(value) && !value.length)) {
      return;
    }
    const firstSupported = restrictedLocales.find((locale) =>
      availableLocales.includes(locale),
    );

    if (Array.isArray(value)) {
      let newValues = value.filter((locale) => allowedLocales.includes(locale));

      if (newValues.length !== value.length) {
        if (!newValues.length) {
          newValues = [firstSupported];
        }
        _onChange(
          newValues
            .map((v: string) => allLocalesOptions.find((o) => o.value === v))
            .filter(Boolean) as OptionsObject[],
        );
      }

      return;
    }
    if (previousCountries === countries && allowedLocales.includes(value)) {
      return;
    }
    if (
      previousCountries !== countries &&
      restrictedLocales.includes(value) &&
      value !== 'en'
    ) {
      return;
    }

    if (value === firstSupported) {
      return;
    }
    onChangeRef.current(
      allLocalesOptions.find((o) => o.value === firstSupported),
    );
  }, [
    value,
    restrictedLocales,
    allowedLocales,
    onChangeRef,
    allLocalesOptions,
    countries,
    previousCountriesRef,
  ]);

  return !restricted ? (
    <Select
      name={name}
      label={label}
      options={allLocalesOptions}
      isMulti={isMulti}
      onChange={_onChange}
      value={value}
      {...props}
    />
  ) : (
    <CheckboxBlockList label={label} fullLine {...props}>
      {restrictedLocales.map((locale) => (
        <CheckboxBlockField
          className={classes.checkbox}
          key={locale}
          value={locale}
          label={'locales.' + locale}
          type={isMulti ? 'checkbox' : 'radio'}
          name={name}
          onChange={onLocaleCheck}
          checked={
            Array.isArray(value) ? value.includes(locale) : value === locale
          }
        />
      ))}
    </CheckboxBlockList>
  );
};
